"use client";

/**
 * URL Synchronization Hook
 *
 * Manages bidirectional synchronization between modal state and URL parameters.
 * Handles URL → modal state and modal state → URL synchronization.
 */

import * as React from "react";
import { useRouter, useSearchParams } from "next/navigation";
import type { GlobalModalId } from "../types.js";
import { GLOBAL_MODAL_IDS } from "../constants.js";

interface UrlSyncConfig {
  syncWithUrl: boolean;
  urlParamName: string;
  defaultReplace: boolean;
  modalStack: any; // Mantine modal stack
}

export interface UrlSyncReturn {
  /** Sync a modal open action to the URL */
  syncModalToUrl: (id: GlobalModalId, options?: { replace?: boolean }) => void;
  /** Sync a modal close action to the URL */
  syncModalCloseToUrl: (id: GlobalModalId, options?: { replace?: boolean }) => void;
  /** Sync all modals closed to the URL */
  syncAllModalsClosedToUrl: () => void;
}

/** No-op URL sync for use in Suspense fallback (avoids useSearchParams during static generation). */
export const NO_OP_URL_SYNC: UrlSyncReturn = {
  syncModalToUrl: () => {},
  syncModalCloseToUrl: () => {},
  syncAllModalsClosedToUrl: () => {},
};

/**
 * Hook for managing URL synchronization with modal state
 *
 * Handles:
 * - Opening modals when URL changes
 * - Updating URL when modals open/close
 * - Preventing circular updates with sync flags
 *
 * @param config - Configuration for URL sync
 * @returns Object with methods to sync modal state to URL
 */
export const useUrlSync = (config: UrlSyncConfig): UrlSyncReturn => {
  const router = useRouter();
  const searchParams = useSearchParams();

  // True while waiting for the router to apply a URL change we initiated.
  const isSyncingRef = React.useRef(false);
  // Target modal id in URL once sync completes (`null` = no modal param).
  const pendingUrlModalRef = React.useRef<string | null | undefined>(undefined);
  // Last URL modal id we already applied to the Mantine stack.
  const lastProcessedUrlModalRef = React.useRef<string | null | undefined>(
    undefined,
  );
  const modalStackRef = React.useRef(config.modalStack);
  const configRef = React.useRef(config);

  // Update refs when dependencies change (but don't trigger callback recreation)
  React.useEffect(() => {
    modalStackRef.current = config.modalStack;
    configRef.current = config;
  }, [config]);

  const readUrlModalId = React.useCallback((): string | null => {
    return searchParams?.get(config.urlParamName) ?? null;
  }, [searchParams, config.urlParamName]);

  // Sync URL → Modal State (when URL changes)
  React.useEffect(() => {
    if (!config.syncWithUrl) return;

    const urlModalId = readUrlModalId();

    // Wait until the URL matches the modal param we wrote during open/close.
    if (isSyncingRef.current) {
      if (urlModalId !== pendingUrlModalRef.current) {
        return;
      }
      isSyncingRef.current = false;
      pendingUrlModalRef.current = undefined;
    }

    if (urlModalId === lastProcessedUrlModalRef.current) {
      return;
    }

    lastProcessedUrlModalRef.current = urlModalId;

    if (urlModalId && GLOBAL_MODAL_IDS.includes(urlModalId)) {
      if (!modalStackRef.current.state[urlModalId]) {
        modalStackRef.current.open(urlModalId);
      }
    } else {
      const openModals = Object.keys(modalStackRef.current.state).filter(
        (id) => modalStackRef.current.state[id],
      );
      if (openModals.length > 0) {
        modalStackRef.current.closeAll();
      }
    }
  }, [searchParams, config.syncWithUrl, config.urlParamName, readUrlModalId]);

  const syncModalToUrl = React.useCallback(
    (id: GlobalModalId, options?: { replace?: boolean }) => {
      const { syncWithUrl, defaultReplace, urlParamName } = configRef.current;
      if (!syncWithUrl || isSyncingRef.current) return;

      const shouldReplace = options?.replace ?? defaultReplace;

      isSyncingRef.current = true;
      pendingUrlModalRef.current = id;

      const params = new URLSearchParams(searchParams?.toString() || "");
      params.set(urlParamName, id);

      const newUrl = `${window.location.pathname}?${params.toString()}`;

      if (shouldReplace) {
        router.replace(newUrl);
      } else {
        router.push(newUrl);
      }
    },
    [searchParams, router],
  );

  const syncModalCloseToUrl = React.useCallback(
    (id: GlobalModalId, options?: { replace?: boolean }) => {
      const { syncWithUrl, defaultReplace, urlParamName } = configRef.current;
      if (!syncWithUrl || isSyncingRef.current) return;

      const currentUrlModal = searchParams?.get(urlParamName);

      if (currentUrlModal === id) {
        const shouldReplace = options?.replace ?? defaultReplace;

        isSyncingRef.current = true;
        pendingUrlModalRef.current = null;

        const params = new URLSearchParams(searchParams?.toString() || "");
        params.delete(urlParamName);

        const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}`;

        if (shouldReplace) {
          router.replace(newUrl);
        } else {
          router.push(newUrl);
        }
      }
    },
    [searchParams, router],
  );

  const syncAllModalsClosedToUrl = React.useCallback(() => {
    const { syncWithUrl, urlParamName } = configRef.current;
    if (!syncWithUrl || isSyncingRef.current) return;

    isSyncingRef.current = true;
    pendingUrlModalRef.current = null;

    const params = new URLSearchParams(searchParams?.toString() || "");
    params.delete(urlParamName);

    const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}`;
    router.push(newUrl);
  }, [searchParams, router]);

  return {
    syncModalToUrl,
    syncModalCloseToUrl,
    syncAllModalsClosedToUrl,
  };
};
